(() => {
  'use strict';

  const safeLocalUrl = (value, base) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, base);
      return url.origin === new URL(base).origin && /^https?:$/.test(url.protocol) ? url.href : '';
    } catch { return ''; }
  };
  const safeImageUrl = (value, base) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, base);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch { return ''; }
  };
  const money = (amount, currency, locale) => new Intl.NumberFormat(locale, {
    style: 'currency', currency
  }).format(Number(amount || 0) / 100);
  const nextQuantity = (current, delta) => Math.max(0, Math.floor(Number(current) || 0) + delta);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { safeLocalUrl, safeImageUrl, money, nextQuantity };
    return;
  }

  const init = () => {
    const config = document.querySelector('[data-ux-panels-config]');
    if (!config || !window.HTMLDialogElement) return;
    const root = `${config.dataset.root.replace(/\/$/, '')}/`;
    const locale = config.dataset.locale || document.documentElement.lang || 'en';
    const currency = config.dataset.currency || 'AED';
    const labels = Object.fromEntries([...config.querySelectorAll('[data-ux-text]')].map((node) => [node.dataset.uxText, node.textContent]));
    const dialogs = new Map([...document.querySelectorAll('dialog[data-ux-panel]')].map((dialog) => [dialog.dataset.uxPanel, dialog]));
    const catalog = new Map([...document.querySelectorAll('template[data-ux-product-template]')].map((template) => [template.dataset.handle, template]));
    const historyToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const historyKey = '__sahravaPanel';
    let active = null;
    let opener = null;
    let historyPending = false;
    let afterHistory = null;
    let searchTimer;
    let searchController;
    let searchVersion = 0;
    let lastCart = null;
    let cartVersion = 0;
    let cartQueue = Promise.resolve();
    let queuedMutations = 0;
    let noteDirty = false;
    let noteTimer;
    let savedNote = null;
    const nativeForms = new WeakSet();
    const searchInput = document.querySelector('[data-ux-search-input]');
    const searchResults = document.querySelector('[data-ux-search-results]');
    const searchStatus = document.querySelector('[data-ux-search-status]');
    const searchAll = document.querySelector('[data-ux-search-all]');
    const wishlistItems = document.querySelector('[data-ux-wishlist-items]');
    const cartItems = document.querySelector('[data-ux-cart-items]');
    const cartForm = document.querySelector('[data-ux-cart-form]');
    const note = document.querySelector('[data-ux-cart-note]');
    const cartStatus = document.querySelector('[data-ux-cart-status]');
    const cartFallback = document.querySelector('[data-ux-cart-fallback]');

    const icon = (name) => document.querySelector(`template[data-ux-icon-${name}]`)?.content.cloneNode(true) || document.createTextNode('');
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const wishlist = () => window.SahravaWishlist?.read?.() || [];
    const updateHearts = (container) => {
      const items = wishlist();
      container.querySelectorAll('[data-wishlist-product]').forEach((item) => {
        const button = item.querySelector('[data-wishlist-toggle]');
        if (!button) return;
        const saved = items.some((value) => value.handle === item.dataset.handle);
        button.setAttribute('aria-pressed', String(saved));
        button.classList.toggle('is-saved', saved);
        if (saved) button.setAttribute('aria-label', `${labels.remove_saved}: ${item.dataset.title}`);
      });
    };
    const ownedState = () => history.state?.[historyKey]?.token === historyToken;
    const setExpanded = (name) => document.querySelectorAll('[data-panel-open]').forEach((control) => control.setAttribute('aria-expanded', String(control.dataset.panelOpen === name)));
    const closeUI = (restore = true) => {
      const previous = active;
      active = null;
      if (previous?.open) previous.close();
      document.documentElement.classList.remove('ux-panel-open');
      setExpanded(null);
      if (restore && opener?.isConnected) opener.focus({ preventScroll: true });
    };
    const closePanel = (after) => {
      if (historyPending) return;
      closeUI(!after);
      if (ownedState()) {
        historyPending = true;
        afterHistory = after || null;
        history.back();
      } else after?.();
    };
    const openPanel = (name, control, push = true) => {
      const dialog = dialogs.get(name);
      if (!dialog || historyPending) return false;
      if (active === dialog) return true;
      document.querySelector('[data-filter-panel]:not([hidden]) [data-filter-close]')?.click();
      if (active) closeUI(false);
      opener = control || document.activeElement;
      active = dialog;
      if (push) {
        const state = { ...(history.state || {}), [historyKey]: { token: historyToken, name } };
        if (ownedState()) history.replaceState(state, '', location.href);
        else history.pushState(state, '', location.href);
      }
      document.documentElement.classList.add('ux-panel-open');
      setExpanded(name);
      dialog.showModal();
      if (name === 'search') {
        searchInput?.focus({ preventScroll: true });
      } else dialog.querySelector('[data-panel-close]')?.focus({ preventScroll: true });
      if (name === 'wishlist') renderWishlist();
      if (name === 'cart') loadCart();
      return true;
    };
    const withSavedNote = async (callback) => {
      window.clearTimeout(noteTimer);
      if (noteDirty) {
        const saved = await queueCart(() => saveNote());
        if (!saved) return;
      }
      callback();
    };
    const navigate = (href) => withSavedNote(() => closePanel(() => location.assign(href)));
    window.addEventListener('popstate', () => {
      if (historyPending) {
        historyPending = false;
        const after = afterHistory;
        afterHistory = null;
        after?.();
        return;
      }
      if (ownedState()) openPanel(history.state[historyKey].name, opener, false);
      else closeUI();
    });
    window.addEventListener('pageshow', () => {
      if (!ownedState() && active) closeUI(false);
    });
    dialogs.forEach((dialog) => {
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closePanel();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closePanel();
      });
    });
    document.addEventListener('click', (event) => {
      const target = event.target.closest?.('[data-panel-open], [data-panel-close], a[href]');
      if (!target || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (target.hasAttribute('data-panel-open')) {
        if (historyPending) { event.preventDefault(); return; }
        if (openPanel(target.dataset.panelOpen, target)) event.preventDefault();
      } else if (target.hasAttribute('data-panel-close')) {
        event.preventDefault();
        closePanel();
      } else if (active?.contains(target) && !target.hasAttribute('download') && (!target.target || target.target === '_self')) {
        const url = safeLocalUrl(target.href, location.href);
        if (!url) return;
        event.preventDefault();
        navigate(url);
      }
    });
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (nativeForms.has(form)) { nativeForms.delete(form); return; }
      const returnTo = form.querySelector('input[name="return_to"]');
      if (returnTo) returnTo.value = `${location.pathname}${location.search}`;
      if (!active?.contains(form) || form === cartForm) return;
      event.preventDefault();
      const submitter = event.submitter;
      withSavedNote(() => closePanel(() => {
        nativeForms.add(form);
        form.requestSubmit(submitter || undefined);
      }));
    });

    const fallbackWishlistItem = (item) => {
      const article = element('article', 'ux-product-item');
      article.dataset.wishlistProduct = '';
      article.dataset.handle = item.handle;
      article.dataset.title = item.title || item.handle;
      const href = safeLocalUrl(item.url, location.href) || `${root}products/${encodeURIComponent(item.handle)}`;
      const imageLink = element('a', 'ux-product-image');
      imageLink.href = href;
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      const source = safeImageUrl(item.image, location.href);
      if (source) {
        const image = element('img');
        image.src = source;
        image.alt = '';
        image.width = 88;
        image.height = 110;
        image.loading = 'lazy';
        imageLink.append(image);
      }
      const copy = element('div', 'ux-product-copy');
      const title = element('h3');
      const titleLink = element('a', '', item.title || item.handle);
      titleLink.href = href;
      titleLink.dir = 'auto';
      title.append(titleLink);
      copy.append(title);
      const button = element('button', 'ux-heart');
      button.type = 'button';
      button.dataset.wishlistToggle = '';
      button.setAttribute('aria-label', `${labels.remove_saved}: ${item.title || item.handle}`);
      button.append(icon('heart'));
      article.append(imageLink, copy, button);
      return article;
    };
    const renderWishlist = () => {
      if (!wishlistItems) return;
      const items = wishlist();
      const focused = document.activeElement?.closest('[data-wishlist-product]');
      const focusedHandle = wishlistItems.contains(focused) ? focused.dataset.handle : null;
      const focusIndex = focusedHandle ? [...wishlistItems.children].findIndex((node) => node.dataset.handle === focusedHandle) : -1;
      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        const template = catalog.get(item.handle);
        fragment.append(template?.content.firstElementChild?.cloneNode(true) || fallbackWishlistItem(item));
      });
      wishlistItems.replaceChildren(fragment);
      document.querySelector('[data-ux-wishlist-empty]').hidden = items.length > 0;
      updateHearts(wishlistItems);
      if (active?.dataset.uxPanel === 'wishlist' && focusIndex >= 0) {
        const replacement = [...wishlistItems.children].find((node) => node.dataset.handle === focusedHandle) || wishlistItems.children[Math.min(focusIndex, items.length - 1)];
        (replacement?.querySelector('[data-wishlist-toggle]') || active.querySelector('[data-panel-close]')).focus({ preventScroll: true });
      }
    };
    document.addEventListener('sahrava:wishlist-change', () => {
      renderWishlist();
      updateHearts(searchResults);
    });

    const localSearchMatches = (query) => {
      const normalized = query.toLocaleLowerCase(locale).trim();
      return [...catalog.values()].filter((template) => template.content.firstElementChild?.dataset.title.toLocaleLowerCase(locale).includes(normalized));
    };
    const performSearch = async () => {
      const version = ++searchVersion;
      searchController?.abort();
      const query = searchInput.value.trim();
      searchResults.replaceChildren();
      searchAll.hidden = !query;
      if (!query) {
        searchStatus.textContent = labels.search_hint;
        searchResults.removeAttribute('aria-busy');
        return;
      }
      const fullUrl = new URL(config.dataset.searchUrl, location.origin);
      fullUrl.searchParams.set('q', query);
      fullUrl.searchParams.set('type', 'product');
      fullUrl.searchParams.set('options[prefix]', 'last');
      searchAll.href = fullUrl.href;
      searchStatus.textContent = labels.loading;
      searchResults.setAttribute('aria-busy', 'true');
      searchController = new AbortController();
      const url = new URL(config.dataset.predictiveUrl, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'ux-predictive-search');
      try {
        const response = await fetch(url, { signal: searchController.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (version !== searchVersion) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const results = parsed.querySelector('[data-ux-search-response]');
        if (!results) throw new Error('Search unavailable');
        const matches = [...results.children];
        localSearchMatches(query).forEach((template) => {
          if (!matches.some((match) => match.dataset.handle === template.dataset.handle)) matches.unshift(template.content.firstElementChild.cloneNode(true));
        });
        searchResults.replaceChildren(...matches.slice(0, 8));
        updateHearts(searchResults);
        const count = searchResults.children.length;
        searchStatus.textContent = count ? `${labels.search_count} ${count}` : labels.search_empty;
      } catch (error) {
        if (error.name === 'AbortError' || version !== searchVersion) return;
        const matches = localSearchMatches(query).slice(0, 8);
        searchResults.replaceChildren(...matches.map((template) => template.content.firstElementChild.cloneNode(true)));
        updateHearts(searchResults);
        searchStatus.textContent = matches.length ? `${labels.search_count} ${matches.length}` : labels.search_error;
      } finally {
        if (version === searchVersion) searchResults.removeAttribute('aria-busy');
      }
    };
    searchInput?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchController?.abort();
      searchVersion += 1;
      searchTimer = window.setTimeout(performSearch, 220);
    });

    const setCartStatus = (message, error = false) => {
      cartStatus.textContent = message || '';
      cartStatus.dataset.error = String(error);
    };
    const setCartBusy = (busy) => {
      cartForm?.setAttribute('aria-busy', String(busy));
      cartForm?.querySelectorAll('[data-ux-cart-change], [data-ux-checkout]').forEach((button) => { button.disabled = busy; });
    };
    const renderCart = (cart) => {
      if (!cart || !Array.isArray(cart.items)) return;
      lastCart = cart;
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = String(cart.item_count);
        count.hidden = cart.item_count === 0;
      });
      if (!noteDirty) {
        note.value = cart.note || '';
        savedNote = cart.note || '';
      }
      const fragment = document.createDocumentFragment();
      cart.items.forEach((item) => {
        const curated = catalog.get(item.handle)?.content.firstElementChild;
        const titleText = curated?.dataset.title || item.product_title || item.title;
        const article = element('article', 'ux-cart-item');
        article.dataset.cartKey = item.key;
        const href = safeLocalUrl(item.url, location.href) || `${root}cart`;
        const imageLink = element('a', 'ux-product-image');
        imageLink.href = href;
        imageLink.tabIndex = -1;
        imageLink.setAttribute('aria-hidden', 'true');
        const source = safeImageUrl(curated?.dataset.image || item.image || item.featured_image?.url, location.href);
        if (source) {
          const image = element('img');
          image.src = source;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 80;
          image.height = 100;
          imageLink.append(image);
        }
        const content = element('div');
        const title = element('h3');
        const link = element('a', '', titleText);
        link.href = href;
        link.dir = 'auto';
        title.append(link);
        content.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') content.append(element('p', 'ux-cart-variant', item.variant_title));
        if (item.selling_plan_allocation?.selling_plan?.name) content.append(element('p', 'ux-cart-selling-plan', item.selling_plan_allocation.selling_plan.name));
        Object.entries(item.properties || {}).forEach(([key, value]) => {
          if (!key.startsWith('_') && value) content.append(element('p', 'ux-cart-properties', `${key}: ${value}`));
        });
        const price = element('p', 'ux-cart-line-price');
        price.append(element('bdi', '', money(item.final_line_price, cart.currency || currency, locale)));
        if (item.original_line_price > item.final_line_price) {
          const original = element('s');
          original.append(element('bdi', '', money(item.original_line_price, cart.currency || currency, locale)));
          price.append(original);
        }
        content.append(price);
        const controls = element('div', 'ux-cart-controls');
        const quantity = element('div', 'ux-cart-quantity');
        quantity.setAttribute('role', 'group');
        quantity.setAttribute('aria-label', `${labels.quantity}: ${titleText}`);
        const quantityButton = (action, value) => {
          const button = element('button');
          button.type = 'button';
          button.dataset.uxCartChange = item.key;
          button.dataset.quantity = String(value);
          button.setAttribute('aria-label', `${labels[action]}: ${titleText}`);
          button.append(icon(action === 'decrease' ? 'minus' : 'plus'));
          return button;
        };
        quantity.append(quantityButton('decrease', nextQuantity(item.quantity, -1)), element('output', '', String(item.quantity)), quantityButton('increase', nextQuantity(item.quantity, 1)));
        const remove = element('button', 'ux-cart-remove', labels.remove);
        remove.type = 'button';
        remove.dataset.uxCartChange = item.key;
        remove.dataset.quantity = '0';
        remove.setAttribute('aria-label', `${labels.remove}: ${titleText}`);
        controls.append(quantity, remove);
        content.append(controls);
        article.append(imageLink, content);
        fragment.append(article);
      });
      cartItems.replaceChildren(fragment);
      cartForm.hidden = cart.item_count === 0;
      document.querySelector('[data-ux-cart-empty]').hidden = cart.item_count !== 0;
      document.querySelector('[data-ux-cart-total]').textContent = money(cart.total_price, cart.currency || currency, locale);
      cartFallback.hidden = true;
      setCartBusy(queuedMutations > 0);
    };
    const getCart = async () => {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(labels.cart_error);
      return response.json();
    };
    const loadCart = async () => {
      await cartQueue;
      const version = ++cartVersion;
      setCartStatus(labels.loading);
      try {
        const cart = await getCart();
        if (version !== cartVersion) return;
        renderCart(cart);
        setCartStatus('');
      } catch {
        if (version !== cartVersion) return;
        setCartStatus(labels.cart_error, true);
        cartFallback.hidden = false;
      }
    };
    const queueCart = (action) => {
      queuedMutations += 1;
      setCartBusy(true);
      const task = cartQueue.then(action).catch(() => {
        setCartStatus(labels.cart_error, true);
        cartFallback.hidden = false;
        return false;
      }).finally(() => {
        queuedMutations -= 1;
        setCartBusy(queuedMutations > 0);
      });
      cartQueue = task;
      return task;
    };
    const postCart = async (endpoint, body) => {
      cartVersion += 1;
      const response = await fetch(`${root}cart/${endpoint}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : labels.cart_error);
      return result;
    };
    const saveNote = async () => {
      if (!noteDirty) return true;
      const desired = note.value;
      try {
        const cart = await postCart('update', { note: desired });
        savedNote = desired;
        noteDirty = note.value !== desired;
        renderCart(cart);
        setCartStatus(labels.note_saved);
        return true;
      } catch {
        setCartStatus(labels.cart_error, true);
        cartFallback.hidden = false;
        return false;
      }
    };
    note?.addEventListener('input', () => {
      noteDirty = note.value !== savedNote;
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(() => queueCart(() => saveNote()), 600);
    });
    note?.addEventListener('blur', () => {
      window.clearTimeout(noteTimer);
      if (noteDirty) queueCart(() => saveNote());
    });
    cartItems?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ux-cart-change]');
      if (!button || button.disabled || queuedMutations) return;
      const key = button.dataset.uxCartChange;
      const quantity = Number(button.dataset.quantity);
      const action = button.getAttribute('aria-label');
      queueCart(async () => {
        setCartStatus(labels.loading);
        try {
          const cart = await postCart('change', { id: key, quantity });
          renderCart(cart);
          setCartStatus(labels.cart_saved);
          const row = [...cartItems.children].find((item) => item.dataset.cartKey === key);
          const focusTarget = [...(row?.querySelectorAll('[data-ux-cart-change]') || [])].find((item) => item.getAttribute('aria-label') === action);
          window.requestAnimationFrame(() => (focusTarget || dialogs.get('cart').querySelector('[data-panel-close]'))?.focus({ preventScroll: true }));
          return true;
        } catch (error) {
          try { renderCart(await getCart()); } catch { cartFallback.hidden = false; }
          setCartStatus(error.message || labels.cart_error, true);
          return false;
        }
      });
    });
    cartForm?.addEventListener('submit', async (event) => {
      if (nativeForms.has(cartForm)) { nativeForms.delete(cartForm); return; }
      event.preventDefault();
      if (queuedMutations || !lastCart?.item_count) return;
      const submitter = event.submitter || cartForm.querySelector('[data-ux-checkout]');
      window.clearTimeout(noteTimer);
      const saved = await queueCart(() => saveNote());
      if (!saved) return;
      closePanel(() => {
        nativeForms.add(cartForm);
        cartForm.requestSubmit(submitter);
      });
    });
    document.addEventListener('sahrava:cart-added', (event) => {
      if (event.detail?.cart) renderCart(event.detail.cart);
      openPanel('cart', document.activeElement);
    });
    window.SahravaPanels = { open: openPanel, close: closePanel };
    renderWishlist();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
